import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Globe, Users, Lightbulb, Target, ArrowRight, Mail, Phone } from 'lucide-react';

const useReveal = (opts = {}) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setVis(true); return; }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.unobserve(el); } },
      { threshold: opts.threshold || 0.12 }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, [opts.threshold]);
  return [ref, vis];
};

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} className={`transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const AboutPage = () => {
  const [pageVisible, setPageVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'About — Insust Global';
    const t = setTimeout(() => setPageVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const values = [
    { icon: MapPin, title: 'Spatial First', description: 'Every solution we build starts with location. We believe the "where" is the most underutilized dimension in enterprise decision-making.' },
    { icon: Target, title: 'Decision-Grade Accuracy', description: 'We don\'t deliver data — we deliver confidence. Every output is validated, explainable, and ready for strategic decisions.' },
    { icon: Lightbulb, title: 'Applied Innovation', description: 'We combine proven geospatial science with modern AI to solve real problems — not chase hype. Practical impact over theoretical elegance.' },
    { icon: Users, title: 'Domain Expertise', description: 'Our team brings deep experience across GIS, remote sensing, urban planning, real estate, and enterprise software engineering.' },
  ];

  const team = [
    { name: 'Subhrajyoti Saha', role: 'Founder, CEO' },
    { name: 'Tushar Barik', role: 'Founder, COO' },
    { name: 'Naveen Krishnaa', role: 'Co-Founder, CTO' },
    { name: 'Asin Raja', role: 'Engineer' },
    { name: 'Suvam Das', role: 'Consultant' },
    { name: 'Rakesh Biswasharma', role: 'Consultant' },
    { name: 'Asim Maity', role: 'LiDAR Expert' },
  ];

  const milestones = [
    { year: '2025', step: '01', title: 'Founded', description: 'Insust Global established with a mission to democratize geospatial intelligence for enterprises.' },
    { year: '2025', step: '02', title: 'First Enterprise Clients', description: 'Delivered basemap and spatial foundation projects across infrastructure, real estate, and government sectors.' },
    { year: '2025', step: '03', title: 'Platform Launch', description: 'Launched the Insust platform — integrating AI, GIS, and decision analytics into a unified solution.' },
    { year: '2025', step: '04', title: 'Property Intelligence', description: 'Released the Property Intelligence Platform — AI-powered real estate investment analysis with MCDM ranking.' },
    { year: '2026', step: '05', title: 'Scaling Solutions', description: 'Expanding across 6 industries with 8 solution verticals, GIS training programs, and upcoming product launches.' },
  ];

  return (
    <div className={`min-h-screen bg-slate-950 transition-opacity duration-700 ${pageVisible ? 'opacity-100' : 'opacity-0'}`}>

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 h-16 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.jpeg" alt="Insust Global logo" className="h-8 w-8 rounded-full object-contain" />
          <span className="text-lg font-bold text-white">Insust Global</span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-teal-600/[0.04] blur-[120px]" />
          <div className="absolute bottom-[-20%] left-[-15%] w-[40vw] h-[40vw] rounded-full bg-cyan-500/[0.03] blur-[100px]" />
        </div>
        <div className="max-w-[900px] mx-auto text-center relative z-10">
          <Reveal>
            <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">About Insust Global</span>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-[1.05] tracking-tight">
              The Intelligence<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-400">of Where.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
              Insust Global builds AI-powered geospatial platforms that transform spatial data into strategic intelligence — enabling enterprises, governments, and institutions to make location-aware decisions with confidence.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-6 lg:px-12 bg-slate-900/50 border-y border-slate-800/50">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">Our Mission</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
                Making spatial intelligence accessible to every organization
              </h2>
              <p className="text-slate-400 leading-relaxed mb-4">
                Location influences everything — from where cities grow and supply chains move, to how markets evolve and risks emerge. Yet most organizations still make critical decisions without spatial context.
              </p>
              <p className="text-slate-400 leading-relaxed">
                We exist to change that. By combining GIS, remote sensing, AI, and decision science, we make geospatial intelligence practical, explainable, and actionable for teams that need it most.
              </p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '8', label: 'Solution Verticals' },
                { value: '6', label: 'Industries Served' },
                { value: '40+', label: 'Countries Covered' },
                { value: '500+', label: 'Projects Delivered' },
              ].map((stat, i) => (
                <div key={i} className="p-6 rounded-xl bg-slate-950 border border-slate-800 text-center">
                  <div className="text-3xl font-bold text-teal-400 mb-1">{stat.value}</div>
                  <div className="text-[10px] font-mono tracking-wider uppercase text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">What Drives Us</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white">Our Values</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="group p-7 rounded-xl border border-slate-800/50 hover:border-teal-500/20 bg-slate-900/20 hover:bg-slate-900/40 transition-all duration-300 h-full">
                    <div className="w-11 h-11 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mb-5 group-hover:bg-teal-500/20 transition-colors">
                      <Icon className="w-5 h-5 text-teal-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{v.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{v.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 px-6 lg:px-12 bg-slate-900/30 border-y border-slate-800/50">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">Our Team</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white">The People Behind Insust Global</h2>
            </div>
          </Reveal>
          <div className="flex flex-wrap justify-center gap-6">
            {team.map((member, i) => (
              <Reveal key={i} delay={i * 80} className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.125rem)]">
                <div className="group p-7 rounded-xl border border-slate-800/60 hover:border-teal-500/30 bg-slate-900/40 hover:bg-slate-900/70 transition-all duration-300 h-full text-center">
                  <h3 className="text-lg font-bold text-white leading-snug">{member.name}</h3>
                  <div className="mx-auto w-10 h-px bg-teal-500/40 my-3" />
                  <p className="text-xs font-mono tracking-wider uppercase text-teal-400/80">{member.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-6 lg:px-12 bg-slate-900/50 border-y border-slate-800/50">
        <div className="max-w-[900px] mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">Our Journey</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white">Milestones</h2>
            </div>
          </Reveal>
          <div className="relative">
            <div className="absolute left-[19px] top-0 bottom-0 w-px bg-slate-800 hidden sm:block" />
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <Reveal key={i} delay={i * 80}>
                  <div className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-950 border-2 border-teal-600 flex items-center justify-center relative z-10">
                      <span className="text-[10px] font-mono text-teal-400 font-bold">{m.step}</span>
                    </div>
                    <div className="pb-2">
                      <div className="flex items-baseline gap-3 mb-1">
                        <span className="text-xs font-mono text-teal-400/60">{m.year}</span>
                        <h3 className="text-base font-bold text-white">{m.title}</h3>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed">{m.description}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-[900px] mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">Get in Touch</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white">Contact Us</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            <Reveal>
              <a
                href="mailto:insust.global@gmail.com"
                className="group flex items-center gap-4 p-6 rounded-xl border border-slate-800/60 hover:border-teal-500/40 bg-slate-900/30 hover:bg-slate-900/60 transition-all"
              >
                <div className="w-11 h-11 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
                  <Mail className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <div className="text-[10px] font-mono tracking-wider uppercase text-slate-500 mb-1">Email</div>
                  <div className="text-sm font-bold text-white group-hover:text-teal-300 transition-colors break-all">insust.global@gmail.com</div>
                </div>
              </a>
            </Reveal>
            <Reveal delay={100}>
              <a
                href="tel:+919434144165"
                className="group flex items-center gap-4 p-6 rounded-xl border border-slate-800/60 hover:border-teal-500/40 bg-slate-900/30 hover:bg-slate-900/60 transition-all"
              >
                <div className="w-11 h-11 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
                  <Phone className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <div className="text-[10px] font-mono tracking-wider uppercase text-slate-500 mb-1">Phone</div>
                  <div className="text-sm font-bold text-white group-hover:text-teal-300 transition-colors">+91 94341 44165</div>
                </div>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-12 text-center">
        <Reveal>
          <div className="max-w-[600px] mx-auto">
            <h2 className="text-3xl font-bold text-white mb-4">Want to work with us?</h2>
            <p className="text-slate-400 mb-8">See how Insust Global can transform spatial data into strategic advantage for your organization.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm rounded-lg transition-all">
                Explore Solutions <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-6 px-6 lg:px-16 flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Insust Global. All rights reserved.</span>
        <div className="flex gap-4 text-xs text-slate-600">
          <Link to="/about" className="text-teal-400/60">About</Link>
          <Link to="/" className="hover:text-teal-400 transition-colors">Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
